import { useQuery } from '@tanstack/react-query';
import { surveyReportApi } from '../api/surveyReportApi';
import { UseSurveyReportOptions } from '../types';

// Query Keys Factory
export const surveyReportQueryKeys = {
  all: ['surveyReports'] as const,
  reports: () => [...surveyReportQueryKeys.all, 'reports'] as const,
  detail: (reportId: string) => [...surveyReportQueryKeys.reports(), reportId] as const,
  byInstanceId: (instanceId: string) =>
    [...surveyReportQueryKeys.all, 'instance', instanceId] as const,
  byAuditId: (auditId: string) => [...surveyReportQueryKeys.all, 'audit', auditId] as const,
  bySurveyId: (surveyId: string) => [...surveyReportQueryKeys.all, 'survey', surveyId] as const,
  bySubjectId: (subjectId: string) => [...surveyReportQueryKeys.all, 'subject', subjectId] as const,
  statistics: (reportId: string) =>
    [...surveyReportQueryKeys.detail(reportId), 'statistics'] as const,
  permissions: (reportId: string) =>
    [...surveyReportQueryKeys.detail(reportId), 'permissions'] as const,
  exports: (reportId: string) => [...surveyReportQueryKeys.detail(reportId), 'exports'] as const,
};

// Get Survey Report by ID
export const useSurveyReport = (reportId: string, options?: UseSurveyReportOptions) => {
  return useQuery({
    queryKey: surveyReportQueryKeys.detail(reportId),
    queryFn: () => surveyReportApi.getSurveyReport(reportId),
    enabled: options?.enabled !== false && !!reportId,
    staleTime: options?.staleTime || 5 * 60 * 1000, // 5 minutes
    refetchInterval: options?.refetchInterval,
    retry: (failureCount, error: any) => {
      // Don't retry if report not found
      if (error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });
};

// Get Report by Audit ID
export const useReportByAuditId = (auditId: string, options?: UseSurveyReportOptions) => {
  return useQuery({
    queryKey: surveyReportQueryKeys.byAuditId(auditId),
    queryFn: () => surveyReportApi.getReportByAuditId(auditId),
    enabled: options?.enabled !== false && !!auditId,
    staleTime: options?.staleTime || 5 * 60 * 1000,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });
};
